import { strings } from '@/data/strings';
import { SectionHeader } from '@/components/SectionHeader';
import { useInView } from '@/hooks/useInView';
import styles from './Trust.module.css';

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(' ');
}

const ICONS = {
  verification: IconShieldCheck,
  privacy: IconLock,
  boundary: IconBoundary,
} as const;

export function Trust() {
  const { ref, inView } = useInView<HTMLDivElement>({
    threshold: 0,
    rootMargin: '0px 0px -25% 0px',
  });

  return (
    <section id="confianza" className={styles.section}>
      <div className="container">
        <SectionHeader
          align="center"
          eyebrow={strings.trust.eyebrow}
          title={strings.trust.title}
          subtitle={strings.trust.subtitle}
        />

        <div ref={ref} className={cx(styles.pillars, inView && styles.revealed)}>
          {strings.trust.pillars.map((pillar) => {
            const Icon = ICONS[pillar.key];
            return (
              <article key={pillar.key} className={styles.pillar} data-pillar={pillar.key}>
                <div className={styles.iconWrap} role="img" aria-label={pillar.iconAlt}>
                  <Icon />
                </div>
                <h3 className={styles.pillarTitle}>{pillar.title}</h3>
                <p className={styles.pillarBody}>{pillar.body}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Iconos — outline, viewBox 40, stroke currentColor.
// Cada path lleva la clase .drawPath para que se "dibuje" al entrar al viewport
// (animación de stroke-dasharray controlada desde el CSS).
// ─────────────────────────────────────────────────────────────────────────────

const ICON_PROPS = {
  width: 40,
  height: 40,
  viewBox: '0 0 40 40',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

function IconShieldCheck() {
  return (
    <svg {...ICON_PROPS}>
      <path className={styles.drawPath} d="M20 5 L32 9 L32 19 C32 26 27 32 20 34 C13 32 8 26 8 19 L8 9 Z" />
      <path className={styles.drawPath} d="M14 19.5 L18.5 24 L26 16" />
    </svg>
  );
}

function IconLock() {
  return (
    <svg {...ICON_PROPS}>
      <path className={styles.drawPath} d="M13 18 L13 13 C13 9.5 16 6.5 20 6.5 C24 6.5 27 9.5 27 13 L27 18" />
      <rect className={styles.drawPath} x="9" y="18" width="22" height="15" rx="2.5" />
      <path className={styles.drawPath} d="M20 24 L20 28" />
    </svg>
  );
}

function IconBoundary() {
  return (
    <svg {...ICON_PROPS}>
      <circle className={styles.drawPath} cx="12" cy="15" r="3.5" />
      <path className={styles.drawPath} d="M6 30 C6 26 8.5 22.5 12 22.5 C15.5 22.5 18 26 18 30" />
      <circle className={styles.drawPath} cx="28" cy="15" r="3.5" />
      <path className={styles.drawPath} d="M22 30 C22 26 24.5 22.5 28 22.5 C31.5 22.5 34 26 34 30" />
      <line className={styles.divider} x1="20" y1="7" x2="20" y2="33" strokeDasharray="2 3" />
    </svg>
  );
}
